import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import './SaveReportPanel.scss';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {useTranslation} from 'react-i18next';
import {
  ActionType,
  ReportTypeCode,
  transition_time_default,
} from '../../../shared/constants';
import {
  useExportDataSaveReport,
  useSavedReportList,
  useSaveReport,
  useSaveTimelineGapReport,
} from '../../../hooks/reporting/useReporting';
import ArrModal from '../../common/arr-modal/ArrModal';
import dayjs from 'dayjs';
import {
  ISearchTermSavePayload,
  IUserLoginActivitySavePayload,
} from '../standard-reports/config/reportTypes';
import {useSearchParams} from 'react-router-dom';
import {useHasMySearch} from '../standard-reports/reports/UseHasMySearch';
import {transformToSaveReportPayload} from '../../../utils/reportPayloadTransformer';
import {
  ISaveReportResponse,
  ISaveTimelineGapReportRequestBody,
} from '../../../api/types/reportingApi.type';

type SaveReportPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  saveReportConfig: IUserLoginActivitySavePayload | ISearchTermSavePayload;
  onSuccess: (action: string) => void;
  onError: (error: any, isUniqueNameError?: boolean) => void;
  canSave?: boolean;
  isExportDataReport?: boolean;
  exportDataReportPayload?: any;
  saveReportType?: 'default' | 'timelineGap';
};

const SaveReportPanel: React.FC<SaveReportPanelProps> = ({
  isOpen,
  onClose,
  saveReportConfig,
  onSuccess,
  onError,
  canSave = false,
  isExportDataReport = false,
  exportDataReportPayload,
  saveReportType = 'default',
}) => {
  const SAVE_AS_NEW = 'new';
  const OVERWRITE = 'overwrite';
  const PUBLIC = 'Public';
  const PRIVATE = 'Private';
  const PUBLIC_PARENT = '0g';
  const PRIVATE_PARENT = '1g';
  const [searchParams] = useSearchParams();
  const savedReportId = searchParams.get('ReportID');
  const [action, setAction] = useState(SAVE_AS_NEW);
  const [reportType, setReportType] = useState(PRIVATE);
  const [reportName, setReportName] = useState('');
  const [selectedReportId, setSelectedReportId] = useState(savedReportId ?? '');
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openPublicReportConfirmation, setOpenPublicReportConfirmation] =
    useState(false);

  const {reportTypeCode, reportId, searchData, updated_search_ID} =
    saveReportConfig || {};

  const {data: savedReports, refetch: refetchSavedReports} = useSavedReportList(
    isExportDataReport ? ReportTypeCode.EXPORT_REPORT : reportTypeCode
  );

  const [hasInitialized, setHasInitialized] = useState(false);

  const {t} = useTranslation();
  const dataAsOf = dayjs().format('YYYY-MM-DDTHH:mm:ss');
  const savedReportParent = savedReports?.find(
    (report) => report.id === savedReportId
  )?.parent;
  let activeParent: typeof PRIVATE | typeof PUBLIC | null = null;

  if (savedReportParent === PRIVATE_PARENT) {
    activeParent = PRIVATE;
  } else if (savedReportParent === PUBLIC_PARENT) {
    activeParent = PUBLIC;
  }

  const actionOptions = [
    {label: t('saveReport.saveAsNew'), value: SAVE_AS_NEW},
    {label: t('saveReport.overwrite'), value: OVERWRITE},
  ];

  const reportTypeOptions = [
    {label: t('saveReport.private'), value: PRIVATE},
    {label: t('saveReport.public'), value: PUBLIC},
  ];
  useEffect(() => {
    if (isOpen && savedReportId) {
      setSelectedReportId(savedReportId);
    }
  }, [isOpen, savedReportId]);
  const handleReportTypeChange = (val: string) => {
    if (!hasInitialized) return;
    setReportType(val);
  };

  useEffect(() => {
    setHasInitialized(true);
    if (action === OVERWRITE) {
      refetchSavedReports?.();
    }
  }, [action, refetchSavedReports]);

  const handleSelected = (value: string[] | null) => {
    setSelectedReportId(value && value.length > 0 ? value[0] : '0');
  };
  const defaultExpandedParentId = savedReports?.find(
    (report) => report.id === savedReportId
  )?.parent;
  const defaultExpandedParent = savedReports?.find(
    (report) => report.id === defaultExpandedParentId
  )?.text;
  const hasMySearch = useHasMySearch(searchData, updated_search_ID);

  const handleSaveReportResponse = (data: ISaveReportResponse) => {
    if (data?.errorCode === '11001000001') {
      onError(t('saveReport.reportNameAlreadyExist'));
    } else if (data?.reportID > 0) {
      onSuccess(action);
      refetchSavedReports?.();
      closeModal();
    }
  };

  const {mutate: saveReport, isPending} = useSaveReport(
    handleSaveReportResponse,
    (error) => {
      onError(error?.message ?? error);
    }
  );

  const {
    mutate: saveTimelineGapReport,
    isPending: isSaveTimelineGapReportLoading,
  } = useSaveTimelineGapReport(handleSaveReportResponse, (error) => {
    onError(error?.message ?? error);
  });

  const {mutate: saveExportDataReport, isPending: isExportPending} =
    useExportDataSaveReport(
      (data) => {
        if (data?.errorCode === 11001000001) {
          onError(t('saveReport.reportNameAlreadyExist'));
        } else if (data?.reportID > 0) {
          onSuccess(action);
          refetchSavedReports?.();
          closeModal();
        }
      },
      (error) => {
        onError(error?.message ?? error);
      }
    );

  const handleSaveReport = () => {
    const isOverwritePublic =
      action === OVERWRITE &&
      savedReports?.find((r) => r.id === selectedReportId)?.parent ===
        PUBLIC_PARENT;

    if (
      hasMySearch &&
      ((action !== OVERWRITE && reportType === PUBLIC) || isOverwritePublic)
    ) {
      onError(
        'Reports with a private search as the source cannot be saved as public.'
      );
      return;
    }

    if (action === OVERWRITE) {
      setOpenConfirmation(true);
    } else if (reportType === PUBLIC) {
      setOpenPublicReportConfirmation(true);
    } else if (isExportDataReport) {
      saveExportDataReportRequest();
    } else {
      saveReportRequest();
    }
  };
  const findReportNameFromReportId = savedReports?.find(
    (report) => report.id === selectedReportId
  );
  const selectedReportName = findReportNameFromReportId?.text || '';

  const saveReportRequest = () => {
    const savePayload = {...saveReportConfig};
    delete savePayload.searchData;

    const selectedReportParent = savedReports?.find(
      (report) => report.id === selectedReportId
    )?.parent;

    const criteriaWithMetadata = {
      ...savePayload,
      dataAsOf,
      isShared:
        action === OVERWRITE
          ? selectedReportParent === PUBLIC_PARENT
          : reportType === PUBLIC,
    };

    const transformedCriteria =
      transformToSaveReportPayload(criteriaWithMetadata);

    const payload = {
      selectionCriteria: transformedCriteria,
      actionType: ActionType.SaveReport,
      reportType: reportTypeCode,
      reportName: action === OVERWRITE ? selectedReportName : reportName.trim(),
      updateOrCreate: action === OVERWRITE,
      reportId: reportId || savePayload.reportID || 0,
    };

    switch (saveReportType) {
      case 'timelineGap': {
        const updatedPayload: {[key in string]: any} = {...payload};
        updatedPayload['timelineFilter'] = {...payload.selectionCriteria};
        delete updatedPayload.selectionCriteria;

        saveTimelineGapReport(
          updatedPayload as ISaveTimelineGapReportRequestBody
        );
        break;
      }
      case 'default':
      default: {
        saveReport(payload);
        break;
      }
    }
  };

  const saveExportDataReportRequest = () => {
    saveExportDataReport({
      ...exportDataReportPayload,
      selectionCriteria: {
        ...exportDataReportPayload?.selectionCriteria,
        isShared: reportType === PUBLIC,
        reportId:
          action === OVERWRITE
            ? Number.parseInt(selectedReportId)
            : exportDataReportPayload?.selectionCriteria?.reportId,
      },
      reportId:
        action === OVERWRITE
          ? Number.parseInt(selectedReportId)
          : exportDataReportPayload?.selectionCriteria?.reportId,
      reportName: action === OVERWRITE ? selectedReportName : reportName.trim(),
      updateOrCreate: action === OVERWRITE,
    });
  };
  const transformSavedReportsToTree = (savedReports: any[]) => {
    const groupedReports = savedReports.reduce((acc, report) => {
      let parent: string | null = null;

      if (report.parent === PRIVATE_PARENT) {
        parent = PRIVATE;
      } else if (report.parent === PUBLIC_PARENT) {
        parent = PUBLIC;
      }

      if (parent) {
        if (!acc[parent]) {
          acc[parent] = [];
        }

        acc[parent].push({
          id: report.id,
          label: report.text,
          type: report.type,
          disabled: !!savedReportId && activeParent && parent !== activeParent,
        });
      }

      return acc;
    }, {});

    const treeData = [PRIVATE, PUBLIC].map((parent) => {
      let folderName = '';
      let isGroup = false;
      let children = [];

      if (parent === PRIVATE) {
        folderName = 'Private Reports';
        isGroup = true;
      } else if (parent === PUBLIC) {
        folderName = 'Public Reports';
        isGroup = true;
      }

      if (groupedReports[parent]) {
        children = groupedReports[parent];
      }

      return {
        id: parent,
        label: folderName,
        isGroup,
        disabled: true,
        children: children,
      };
    });

    return treeData;
  };

  const treeData = savedReports
    ? transformSavedReportsToTree(savedReports)
    : [];

  const closeModal = () => {
    setSelectedReportId('');
    setReportName('');
    setAction(SAVE_AS_NEW);
    onClose?.();
  };

  const handleYesClick = () => {
    setOpenConfirmation(false);
    isExportDataReport ? saveExportDataReportRequest() : saveReportRequest();
  };
  const handleNoClick = () => {
    setOpenConfirmation(false);
  };

  const handlePublicReportNoClick = () => {
    setOpenPublicReportConfirmation(false);
  };

  const handlePublicReportYesClick = () => {
    setOpenPublicReportConfirmation(false);
    isExportDataReport ? saveExportDataReportRequest() : saveReportRequest();
  };

  return (
    <ArrDrawer
      side="right"
      showButton={false}
      isOpen={isOpen}
      onClose={closeModal}
      size="small"
      showHeader={false}
      transition={transition_time_default}
    >
      <Box className="save-report-panel">
        <Box className="custom-header">
          <Typography
            className="header-title"
            tabIndex={0}
            data-testid={`save-report-panel-title`}
          >
            {t('saveReport.title')}
          </Typography>
          <Box className="header-actions">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={closeModal}
              className="cancel-btn"
              dataTestId="save-report-panel-cancel-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              className="save-btn"
              onClick={handleSaveReport}
              disabled={
                isPending ||
                isExportPending ||
                isSaveTimelineGapReportLoading ||
                !canSave ||
                action === SAVE_AS_NEW
                  ? reportName.trim() === ''
                  : !selectedReportId
              }
              dataTestId="save-report-panel-save-button"
            >
              {t('common.save')}
            </ArrButton>
          </Box>
        </Box>

        <Box className="form-section">
          <ArrRadio
            dataTestId="action"
            name="action"
            value={action}
            onChange={setAction}
            data={actionOptions}
            size="medium"
          />
        </Box>

        {action === SAVE_AS_NEW ? (
          <>
            <Box>
              <ArrTextField
                placeholder={t('saveReport.customReport')}
                value={reportName ?? t('saveReport.customReport')}
                onChange={(e) => setReportName(e.target.value)}
                sizeVariant="large"
                state="enabled"
                className="dark-input"
                label={t('saveReport.reportName')}
                title={t('saveReport.reportName')}
                dataTestId="save-report-name"
              />
            </Box>

            <Box className="form-section report-type">
              <ArrRadio
                dataTestId={reportType}
                name="reportType"
                value={reportType}
                onChange={handleReportTypeChange}
                data={reportTypeOptions}
                size="medium"
              />
            </Box>
          </>
        ) : (
          <Box className="over-write-reports">
            <Typography
              variant="body2"
              className="form-label"
              tabIndex={0}
              data-testid={`save-report-panel-select`}
            >
              {t('saveReport.select')}
            </Typography>
            <Box className="report-tree-view">
              <ArrTreeView
                setSelectedItem={handleSelected}
                itemsList={treeData}
                selectedItem={selectedReportId}
                handleNoResultMessage={t('saveReport.noReports')}
                allowGlobalSelect={false}
                treeSelectedItems={[]}
                multipleSelection={false}
                isShowCountLabel={true}
                isShowCheckbox={false}
                isSingleSelectionOnCtrl
                defaultExpandedIds={[defaultExpandedParent ?? '']}
              />
            </Box>
          </Box>
        )}
      </Box>
      <ArrModal
        showModal={openConfirmation}
        className="modal-box"
        onClose={handleNoClick}
        headerTitle={t('saveReport.confirm')}
        title={t('saveReport.modalTitle')}
        description={t('saveReport.modalDescText')}
        dataTestId="savereport-modal"
        buttons={[
          {
            id: 'no',
            label: t('common.no'),
            onClick: handleNoClick,
            variant: 'secondary',
            dataTestId: 'sr-no-button',
          },
          {
            id: 'yes',
            label: t('saveReport.yes'),
            onClick: handleYesClick,
            variant: 'primary',
            dataTestId: 'sr-yes-button',
          },
        ]}
      />
      <ArrModal
        showModal={openPublicReportConfirmation}
        className="modal-box"
        onClose={handlePublicReportNoClick}
        headerTitle={t('saveReport.confirm')}
        title={t('saveReport.modalTitle')}
        description={t('saveReport.publicReportDescText')}
        dataTestId="publicreport-conf-modal"
        buttons={[
          {
            id: 'no',
            label: t('common.no'),
            onClick: handlePublicReportNoClick,
            variant: 'secondary',
            dataTestId: 'prc-no-button',
          },
          {
            id: 'yes',
            label: t('saveReport.yes'),
            onClick: handlePublicReportYesClick,
            variant: 'primary',
            dataTestId: 'prc-yes-button',
          },
        ]}
      />
    </ArrDrawer>
  );
};

export default SaveReportPanel;
